import { Component, JSX, ReactNode } from 'react';
import { Subscription } from 'rxjs';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { GAService } from '../../../services/analytics.service';
import { EnvironmentService } from '../../../services/environment.service';
import { FileDownloadService } from '../../../services/file-download.service';
import { FileManagerService } from '../../../services/file-manager.service';
import { FileProcessService } from '../../../services/file-process.service';
import { ProcessCompleteCheckService } from '../../../services/process-complete-check.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { GAContants } from '../../../types/ga.types';
import { RouteProps } from '../../../types/route-types';
import {
    ProcessStageSubStateTypes,
    ProcessStageTypes,
    ProcessTypes,
    StatusTypes,
    WebsocketProcessTypes,
    WebsocketResponse,
} from '../../../types/types';
import { ConvertOptionsUtils } from '../../../utils/convert-options-utils';
import { PSDate, PSRedableDuration } from '../../../utils/date.utils';
import { FilenameUtils } from '../../../utils/filename-utils';
import { SafeExecUtils } from '../../../utils/safe-exec.utils';
import { StringUtils } from '../../../utils/string-utils';
import { FileUploadReEditModalComponent } from '../file-upload-reedit/file-upload-reedit-modal.component';
import { MediaPreviewModalComponent } from '../media-preview/media-preview-modal.component';
import Modal from '../modal/modal.component';
import ProgressComponent from '../progress/progress.component';
import { RippleLoaderComponent } from '../ripple-loader/ripple-loader.component';
import { SingleFileDownloadModalComponent } from '../single-fle-download/single-file-download.component';
import { get, isNaN, isNil, isNumber, isString, toInteger } from '../../../utils/lodash-min';
import styles from './file-list-item.component.module.scss';
import Tooltip from '../tooltip/tooltip.component';
import { OptionalChildrenProps } from '../../../types/react.types';
import { filesize } from 'filesize';

declare var gapi;
declare var Dropbox;
declare var FB

interface FileListItemComponentProps extends RouteProps, OptionalChildrenProps {
    statusCheckButtonEnabled?: boolean;
    reeditButtonEnabled?: boolean;
    deleteButtonEnabled?: boolean;
    fileUpload?: FileUpload;
    hideFooterButtons?: boolean;
}
interface FileListItemComponentState {
    zipDownloadInProgress: boolean;
}

export class FileListItemComponent extends Component<FileListItemComponentProps, FileListItemComponentState> {
    private readonly logger = Logger.getLogger('FileListItemComponent');
    readonly FILEUPLOAD_STATE_FAILED = StatusTypes.FAILED;

    fileProcessInfo: string;
    fileOutputInfo: string | JSX.Element;

    sub: Subscription;

    get fileUpload() {
        return this.props.fileUpload;
    }

    get hasZipFileInfo() {
        return this.props.fileUpload && this.props.fileUpload.zipResult;
    }

    get zipSupported() {
        return this.fileUpload && (this.fileUpload.processType === ProcessTypes.SPLIT ||
            (this.fileUpload.ws && this.fileUpload.ws.processType === WebsocketProcessTypes.CONVERT_ONE_TO_MANY));
    }

    get canRequestZipFile() {
        return this.zipSupported && !this.fileUpload.zipResult;
    }

    get isZipFileReady() {
        return this.hasZipFileInfo && this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED;
    }

    get hasZipFileData() {
        if (!this.hasZipFileInfo) {
            return 'No data';
        }
        if (this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
            return 'File zipped successfully';
        }
        return this.fileUpload.zipResult.data;
    }

    get statusCheckButtonEnabled() {
        return isNil(this.props.statusCheckButtonEnabled) || this.props.statusCheckButtonEnabled;
    }
    get reeditButtonEnabled() {
        return isNil(this.props.reeditButtonEnabled) || this.props.reeditButtonEnabled;
    }
    get deleteButtonEnabled() {
        return isNil(this.props.deleteButtonEnabled) || this.props.deleteButtonEnabled;
    }

    get hasMultipleFilesToDownload(): boolean {
        return this.fileUpload && this.fileUpload.result && Array.isArray(this.fileUpload.result.output);
    }

    @lazyInject(IOC_TYPES.FileDownloadService) private fileDownloadService: FileDownloadService;
    @lazyInject(IOC_TYPES.GAService) private gaService: GAService;
    @lazyInject(IOC_TYPES.UIContext) private uiContext: UIContext;
    @lazyInject(IOC_TYPES.ProcessCompleteCheckService) private processCompleteCheckService: ProcessCompleteCheckService;
    @lazyInject(IOC_TYPES.ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(IOC_TYPES.SpinnerService) private spinner: SpinnerService;
    @lazyInject(IOC_TYPES.FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(IOC_TYPES.FileProcessService) private fileProcessService: FileProcessService;
    @lazyInject(IOC_TYPES.EnvironmentService) private envService: EnvironmentService;

    readonly state: Readonly<FileListItemComponentState> = { zipDownloadInProgress: false };
    constructor(props) {
        super(props);
    }

    componentDidMount() {
        // this.sub = interval(2000).pipe(
        // ).subscribe(
        //     () => {
        //         this.fileUploadRecoveryService.replayHistoryFor(this.fileUpload).then(
        //             data => {
        //                 try {
        //                     if (data.find(d => (d.processStageSubState && d.processStageSubState.toLowerCase() === 'completed') && (d.processStage && d.processStage.toLowerCase() === 'completed'))) {
        //                         RxUtils.safeCloseSubscriber(this.sub);
        //                     }
        //                 } catch (e) { }
        //             },
        //         );
        //     },
        // );

        try {
            FB.XFBML.parse();
        } catch (e) {
            console.log(e)
        }
    }

    componentWillUnmount() {
        // RxUtils.safeUnsubscribe(this.sub);
    }

    getZipFileURL(fileInfo: FileUpload) {
        if (!fileInfo.result) {
        } else if (fileInfo.result.error === true) {
        } else {
            try {
                const tokens = fileInfo.result.zip.split('\\');
                return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
            } catch (e) {
                return;
            }
        }
    }

    async reedit(fileUpload: FileUpload) {
        await this.initializeScriptsAndWebsockets();
        const initialState = {
            fileUpload,
        };
        Modal.createModal(FileUploadReEditModalComponent, initialState);
    }

    private async initializeScriptsAndWebsockets() {
        await this.spinner.runWithLoader(async () => {
            try {
                await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            } catch (e) {
                this.logger.error({ message: 'Something went wrong while loading scripts.', error: e });
            }
        }, 'Please wait while we initialize file upload service...');
    }

    hasDirectDownloadLink(fileUpload: FileUpload): boolean {
        if (!this.isFileReady(fileUpload) || fileUpload.result.error === true) {
            return false;
        }

        if (Array.isArray(fileUpload.result.output)) {
            return false;
        }
        return true;
    }

    getRangeSupportedDirectDownloadLink(fileUpload: FileUpload): string {
        if (!this.hasDirectDownloadLink(fileUpload)) {
            return null;
        }
        if (Array.isArray(fileUpload.result.output)) {
            return null;
        }

        const tokens = fileUpload.result.output.split('\\');
        const link = this.fileDownloadService.getRangeSupportedDirectDownloadLink(fileUpload, tokens[tokens.length - 1]);
        setTimeout(() => {
            gapi.savetodrive.go(`id${this.props.fileUpload.id}`);
        }, 100);
        return link;
    }

    async onDirectDownloadBtnHandler(fileInfo: FileUpload) {
        await this.scriptLoadService.loadAllScriptsIfNotLoaded();
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                const link = this.fileDownloadService.getDirectDownloadLink(fileInfo, tokens[tokens.length - 1]);
                this.uiContext.showSuccessDialog("Download Links", null, `
                    <div style="text-align: left; word-break: break-all;">
                        <div>
                            Filename: 
                            <div class="alert alert-info">${fileInfo.name}</div>
                        </div>
                        <div>
                            Link:
                            <div class="alert alert-info" style="">${link}</div>
                        </div>
                        <div>*To download file directly, copy and paste above link to your browser's address bar.</div>
                    </div>
                `);
            }
        }
    }


    onDownloadBtnHandler(fileInfo: FileUpload) {
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
            }
        }
    }

    async onZipDownloadBtnHandler(fileInfo: FileUpload) {
        if (!fileInfo.result) {
            this.uiContext.warnToast('Processing File',
                'We are processing your file. Please wait...', false, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast('Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`, true, true);
        } else {
            await this.initializeScriptsAndWebsockets();
            if (this.isZipFileReady) {
                this.openZippedFileDownloadWindow(fileInfo);
            } else if (!this.canRequestZipFile) {
                this.uiContext.warnToast('Processing File',
                    'We are processing your file. Please wait...', false, true);
                return;
            } else {
                this.spinner.runWithLoader(async () => {
                    this.setState({ zipDownloadInProgress: true });
                    await this.fileProcessService.createZip(fileInfo).then(() => {
                        this.logger.trace1('success');
                        this.openZippedFileDownloadWindow(fileInfo);
                        this.setState({ zipDownloadInProgress: false });
                    }).catch((err) => {
                        this.logger.error(err);
                        this.setState({ zipDownloadInProgress: false });
                    });
                    console.log('Zip completed');
                }, 'Zipping files for downloading..');
            }

        }
    }

    private openZippedFileDownloadWindow(fileInfo) {
        const tokens = fileInfo.result.zip.split('\\');
        const downloadWindowOpened = this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
        if (!downloadWindowOpened) {
            this.uiContext.warnToast('Pop-up blocked',
                'If file upload failed. Please make sure your browser not blocking pop-ups...', false, true);
        }
        return;
    }

    getDownloadURL(fileInfo: FileUpload) {
        if (!this.isFileReady(fileInfo)) {
        } else if (fileInfo.result.error === true) {
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                return 'modal';
            } else {
                try {
                    const tokens = fileInfo.result.output.split('\\');
                    return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
                } catch (e) {
                    this.logger.error({ message: `Failed to decode result.output`, error: e, data: FileUpload.toLoggableObject(fileInfo) });
                    return;
                }
            }
        }
    }

    /**
     * Not used and not tested
     * @param fileInfo 
     * @returns 
     */
    getDownloadURLWithFilenameAsAQueryParam(fileInfo: FileUpload) {
        if (!this.isFileReady(fileInfo)) {
        } else if (fileInfo.result.error === true) {
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                return 'modal';
            } else {
                try {
                    const tokens = fileInfo.result.output.split('\\');
                    return this.fileDownloadService.getDownloadURLWithFilenameAsAQueryParam(fileInfo, tokens[tokens.length - 1]);
                } catch (e) {
                    this.logger.error({ message: `Failed to decode result.output`, error: e, data: FileUpload.toLoggableObject(fileInfo) });
                    return;
                }
            }
        }
    }

    getOutputInfo(fileUpload: FileUpload): JSX.Element | string {
        if (!this.fileOutputInfo) {
            this.fileOutputInfo = this._getOutputInfo(fileUpload);
        }
        return this.fileOutputInfo;
    }

    private _getOutputInfo(fileUpload: FileUpload) {
        if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const key = Object.keys(fileUpload.result.sizes.outputs);
            const outputSize = parseFloat(fileUpload.result.sizes.outputs[key[0]]);
            const ratio = parseFloat(1 * (100 * (1 - outputSize / fileUpload.size)) + '').toFixed(2);
            const ratio2 = parseFloat(1 * (100 * (outputSize / fileUpload.size)) + '').toFixed(2);

            return <div>
                {/* <div>Original size: {this.getFileSize(fileUpload.size)}</div> */}
                <div>New size:  {this.getFileSizeInfo(fileUpload)} ({ratio2}% of the original {this.getFileSize(fileUpload.size)})</div>
            </div>;
        }
        return `Output Size: ${this.getFileSizeInfo(fileUpload)}`;
    }

    private getFileSizeInfo(fileUpload: FileUpload) {
        if (fileUpload.processType !== ProcessTypes.SPLIT) {
            try {
                const key = Object.keys(fileUpload.result.sizes.outputs);
                return this.getFileSize(fileUpload.result.sizes.outputs[key[0]]);
            } catch (e) {
                this.logger.error({ message: e.message, error: e });
            }
        }
        return '';
    }

    private getFileSize(size) {
        return filesize(size, { base: 2, standard: "jedec" });
    }

    isSingleFile(outputs: any) {
        return Object.keys(outputs).length === 1;
    }

    decodeDataType(fileUpload: FileUpload, dataType: string, data: string) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (dataType === 'notification') {
            if (wsData.processStage == ProcessStageTypes.UPLOAD) {
                if (wsData.processStageSubState == ProcessStageSubStateTypes.COMPLETED) {
                    const totalFileSize = SafeExecUtils.exec(() => fileUpload.originalInputFiles.map(f => f.metadata.fsize).reduce((p, c) => p + c, 0), null, -1);
                    const dataVal = toInteger(data);
                    if (totalFileSize !== -1 && isNumber(totalFileSize) && !isNaN(totalFileSize) && isNumber(dataVal) && !isNaN(dataVal)) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                    return 'File sent to worker';
                } else if (wsData.processStageSubState == ProcessStageSubStateTypes.PARTIAL) {
                    const totalFileSize = SafeExecUtils.exec(() => fileUpload.originalInputFiles.map(f => f.metadata.fsize).reduce((p, c) => p + c, 0), null, -1);
                    const dataVal = toInteger(data);
                    if (totalFileSize !== -1 && isNumber(totalFileSize) && !isNaN(totalFileSize) && isNumber(dataVal) && !isNaN(dataVal)) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                }
                return 'Sending file to worker';
            } else if (wsData.processStage == ProcessStageTypes.PREPROCESS || wsData.processStage == ProcessStageTypes.PROCESSING) {
                return data;
            }
            if (wsData.processStage == ProcessStageTypes.COMPLETED && data.toLocaleLowerCase() === 'completed') {
                const startTimeInMilli = PSDate.from(fileUpload.uploadStartTime).getTime();
                if (fileUpload.processEndTime) {
                    const endTimeInMilli = PSDate.from(fileUpload.processEndTime).getTime();
                    const prettyTimeMs = new PSRedableDuration(PSDate.from(fileUpload.uploadStartTime))
                        .fromDate(PSDate.from(fileUpload.processEndTime));
                    return `${data}: Took ${prettyTimeMs}`;
                }
                const currentTimeInMilli = PSDate.from().toDate().getTime();
                const prettyTime = new PSRedableDuration(PSDate.from().subtract(currentTimeInMilli - startTimeInMilli, 'ms'))
                    .fromNow();
                return `${data} ${prettyTime}`;
            }
            return data || 'Server Processing File';
        } else if (dataType === 'ratio') {
            return 'Server Processing File';
        } else if (dataType === 'percentage') {
            const message = get(fileUpload, 'ws.readableProcessStage') || 'Server Processing File';
            return message;
        } else {
            return 'Waiting for server to reply';
        }
    }

    getProcessInfo(fileUpload: FileUpload): string {
        if (!this.fileProcessInfo) {
            this.fileProcessInfo = this._getProcessInfo(fileUpload);
        }
        return this.fileProcessInfo;
    }

    private _getProcessInfo(fileUpload: FileUpload): string {
        const inputExtension = FilenameUtils.getExtension(fileUpload.name);
        if (fileUpload.processType === ProcessTypes.SPLIT) {
            const splitOptions = ConvertOptionsUtils.getPropertyValue(fileUpload, 'split');
            if (splitOptions && splitOptions.formValue === 'page_range') {
                return `Extracted PDF ${splitOptions.formChildren[0].formValue} to ${splitOptions.formChildren[1].formValue}`;
            }
            if (fileUpload.fromType.includes('archive')) {
                return `Extracted to individual files`;
            }
            return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}ted PDF to individual pages`;
        } else if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const outputQualityValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQuality')
                || ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQualityAndSize');

            const crfValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'crf')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'crf');
            const presetValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'preset')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'preset');
            const qminValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmin')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmin');
            const qmaxValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmax')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmax');

            const minrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            const maxrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            if (outputQualityValue && isString(outputQualityValue)) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with quality
                            ${outputQualityValue === '-1' ? 'unchanged' : outputQualityValue + '%'}`;
            } else if (crfValue && presetValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                             ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and Preset ${presetValue}`;
            } else if (crfValue && qminValue && qmaxValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                            Quality ${qminValue}-${qmaxValue}%`;
            } else if (crfValue && minrate || minrate) {
                if (minrate &&
                    minrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate) - ${maxrate} (maxrate)`;
                } else if (maxrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${maxrate} (maxrate)`;
                } else {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate)`;
                }

            }
        } else if (fileUpload.processType === ProcessTypes.MERGE) {
            return `${StringUtils.capitalizeFirstLetter(
                this.getProcessTypePastTense(fileUpload.processType))} ${fileUpload.fromType[0].toUpperCase()} files to ${fileUpload.toType.toUpperCase()}`;
        }
        let extension = FilenameUtils.getExtension(ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputExtension'));
        try {
            if (extension === 'original') {
                extension = FilenameUtils.getExtension(fileUpload.originalInputFiles[0].originalName);
            }
        } catch (e) { }
        return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
             ${inputExtension.toLocaleUpperCase()} to ${extension.toLocaleUpperCase()}`;
    }

    getProcessTypePastTense(pType: string) {
        if (/resize$/i.test(pType)) {
            return 'resized';
        } if (/merge$/i.test(pType)) {
            return 'merged';
        } else {
            return pType;
        }
    }

    decodeData(fileUpload: FileUpload) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (wsData.dataType === 'percentage') {
            if (wsData.processStage === 'processing') {
                if (wsData.data.match(/^completed$/i)) {
                    return '100';
                }
                const val = parseFloat(wsData.data).toFixed(2);
                return val;
            }
        } else if (wsData.dataType === 'notification') {
            if (wsData.processStage === 'upload') {
                if (wsData.processStageSubState.match(/^completed$/i)) {
                    return '100';
                }
                const val = Number(100 * parseFloat(wsData.data) / fileUpload.size).toFixed(2);
                return val;
            }
            return wsData.data;
        } else if (wsData.dataType === 'ratio') {
            return wsData.data;
        } else if (wsData.dataType === 'percentage') {
            if (wsData.data.match(/^completed$/i)) {
                return '100';
            }
            const val = parseFloat(wsData.data).toFixed(2);
            return val;
        } else {
            return 'Waiting for server to reply';
        }
    }

    isFileReady(fileInfo: FileUpload) {
        if (!fileInfo.result || (fileInfo.result && Object.keys(fileInfo.result).length === 0)) {
            return false;
        }
        return true;
    }

    checkStatus(fileInfo: FileUpload) {
        return this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
            fileInfo.endpointData.processorMetadataList[0].processDomain,
            fileInfo.id,
        ).then(data => this.logger.trace7(data))
            .catch(err => this.logger.error({ error: err, message: `Error while checking status ${err.message}` }));
    }

    async checkStatusAndShowMessage(fileInfo: FileUpload) {
        try {
            await this.initializeScriptsAndWebsockets();
            this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
                fileInfo.endpointData.processorMetadataList[0].processDomain,
                fileInfo.id,
            ).then(stateRespose => {
                if (stateRespose.state === 'success') {
                    this.uiContext.successToast(
                        'Process Success',
                        'Process successfully completed. Please refresh page.',
                    );
                } else if (stateRespose.state === 'error') {
                    this.uiContext.errorToast(
                        'Error',
                        'Process failed.',
                        false,
                        false,
                    );
                } else {
                    this.uiContext.warnToast(
                        'Unknown',
                        'It\'s looks like server still processing file.',
                        false,
                        false,
                    );
                }
            }).catch(err => {
                this.uiContext.handleError(err);
            }).finally(() => {
                this.forceUpdate();
            });
        } catch (e) {
            this.uiContext.handleError(e);
        }
    }

    getFileDownloadBtnText(fileUpload: FileUpload) {
        const numberOfOutputFiles = get(fileUpload, 'result.outputFiles', []).length;
        if (numberOfOutputFiles > 1) {
            return `Show Downloads (${numberOfOutputFiles} files)`
        }
        return 'Download';
    }

    getOutputFileNameIfAvailable(fileUpload: FileUpload) {
        let filename = fileUpload.name;
        if (fileUpload.processType !== ProcessTypes.SPLIT) {
            let extension = ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputExtension');
            // const extension = fileUpload.options.find(i => i.outputExtension).outputExtension.formValue;
            try {
                if (extension === 'original') {
                    extension = FilenameUtils.getExtension(fileUpload.originalInputFiles[0].originalName);
                }
            } catch (e) { }
            filename = FilenameUtils.replaceExtension(fileUpload.name, extension);
            const numberOfOutputFiles = get(fileUpload, 'result.outputFiles', []).length;
            if (numberOfOutputFiles > 1) {
                filename = `${filename} (${numberOfOutputFiles} files)`
            }
        }
        return filename;
    }

    async removeFile(fileUpload: FileUpload) {
        await this.initializeScriptsAndWebsockets();
        this.uiContext.showConfirmationWarningDialog(
            'Delete File',
            'Are you sure you want to delete?',
            async () => {
                await this.fileManagerService.removeFile(fileUpload);
            },
        );
    }

    playVideo(fileUpload: FileUpload) {
        if (!this.isFileReady(fileUpload)) {
            this.uiContext.errorToast('Not Ready Yet!', '', false, false);
        } else {
            const initialState = {
                url: this.getRangeSupportedDirectDownloadLink(fileUpload),
                mediaType: 'video',
            };
            Modal.createModal(MediaPreviewModalComponent, initialState);
        }
    }

    imagePreview(fileUpload: FileUpload) {
        if (!this.isFileReady(fileUpload)) {
            this.uiContext.errorToast('Not Ready Yet!', '', false, false);
        } else {
            const initialState = {
                url: this.getDownloadURL(fileUpload),
                mediaType: fileUpload.toType === 'pdf' ? 'pdf' : 'image',
            };
            Modal.createModal(MediaPreviewModalComponent, initialState);

        }
    }

    public getFileUpload(): FileUpload {
        return this.fileUpload;
    }

    private isPreviewEnabled(): boolean {
        // TODO mount local folder to docker and enable this
        return false;
    }

    async saveToDropbox(filename: string, downloadLink: string) {
        const instant = await this.spinner.show('Uploading to Dropbox');
        var options = {
            files: [
                // You can specify up to 100 files.
                { 'url': downloadLink, 'filename': filename },
                // ...
            ],

            // Success is called once all files have been successfully added to the user's
            // Dropbox, although they may not have synced to the user's devices yet.
            success: () => {
                // Indicate to the user that the files have been saved.
                this.spinner.hide(instant);
            },

            // Progress is called periodically to update the application on the progress
            // of the user's downloads. The value passed to this callback is a float
            // between 0 and 1. The progress callback is guaranteed to be called at least
            // once with the value 1.
            progress: (progress) => {
                this.spinner.updateMessage(instant, `Uploading to Dropbox. ${progress * 100} completed.`);
                if (progress === 1) {
                    this.spinner.hide(instant);
                }
            },

            // Cancel is called if the user presses the Cancel button or closes the Saver.
            cancel: () => {
                this.spinner.hide(instant);
            },

            // Error is called in the event of an unexpected response from the server
            // hosting the files, such as not being able to find a file. This callback is
            // also called if there is an error on Dropbox or if the user is over quota.
            error: (errorMessage) => {
                this.spinner.hide(instant);
                this.uiContext.showErrorDialog({ title: 'Error', text: errorMessage });
            }
        };
        Dropbox.save(downloadLink, filename, options);
    }

    getFooterButtons(fileUpload) {
        return <div className={styles['footer-buttons']} key={'file-list-item-' + fileUpload.id}>
            <div className={styles['action-btns']}>
                {
                    this.zipSupported && <button
                        disabled={this.state.zipDownloadInProgress || !this.isFileReady(fileUpload) || null}
                        data-url={this.getZipFileURL(fileUpload)}
                        className={`zip-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline download-btn zip-download ${!fileUpload.result || (fileUpload.result && !fileUpload.result.zip) ? 'hide' : ''}`}
                        onClick={() => this.onZipDownloadBtnHandler(fileUpload)}
                    >
                        Download All (Zip)
                        {/* <m-svg fill="white" name="archive"></m-svg> */}
                    </button>
                }


                <button data-url={this.getDownloadURL(fileUpload)}
                    className={`download-htn x-btn x-btn-sm x-btn-primary ${styles['download-btn']}`}
                    onClick={() => this.onDownloadBtnHandler(fileUpload)}
                    disabled={!this.isFileReady(fileUpload)}>
                    {/* <m-svg fill="white" name="cloud_download" aria-hidden="true" aria-hidden="true"></m-svg> */}
                    {this.getFileDownloadBtnText(fileUpload)}
                </button>

                {
                    false && this.hasDirectDownloadLink(fileUpload) && <div style={{ display: 'inline-block' }}>
                        <div className="g-savetodrive"
                            data-src={this.getRangeSupportedDirectDownloadLink(fileUpload)}
                            data-filename={fileUpload.originalInputFiles[0].originalName}
                            data-sitename="www.xconvert.com">
                        </div>
                    </div>
                }

                {
                    this.hasDirectDownloadLink(fileUpload) && <Tooltip text="Save To Dropbox">
                        <button
                            className={`direct-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline x-btn-img`}
                            disabled={!this.isFileReady(fileUpload)}
                            onClick={() => this.saveToDropbox(fileUpload.originalInputFiles[0].originalName, this.getRangeSupportedDirectDownloadLink(fileUpload))} >
                            <img className={styles['dropdox-image']} src="/img/dropbox.png" />
                        </button>
                    </Tooltip>
                }

                <Tooltip text="Downloadable Link">
                    <button data-url={this.getDownloadURL(fileUpload)}
                        className={`download-link-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline x-btn-svg ${styles['download-link-view-btn']}`}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.onDirectDownloadBtnHandler(fileUpload)}>
                        <img src="/icons/svg/link-45deg.svg" />
                    </button>
                </Tooltip>

                {
                    this.statusCheckButtonEnabled && <Tooltip text="Check Status">
                        <button
                            className="status-check-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline x-btn-svg"
                            onClick={() => this.checkStatusAndShowMessage(fileUpload)}>
                            {/* <m-svg fill="white" name="adjest" aria-hidden="true"></m-svg>  */}
                            <img src="/icons/svg/question.svg" />
                        </button>
                    </Tooltip>
                }
                {
                    this.reeditButtonEnabled && <Tooltip text="Re-edit">
                        <button className="re-edit-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline x-btn-svg-with-text"
                            onClick={() => this.reedit(fileUpload)}>
                            <img src="/icons/svg/pencil-square.svg" /> Re-edit
                        </button>
                    </Tooltip>
                }
                {
                    this.deleteButtonEnabled && <Tooltip text="Delete">
                        <button className="delete-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline x-btn-svg"
                            onClick={() => this.removeFile(fileUpload)}>
                            <img src="/icons/svg/trash.svg" />
                        </button>
                    </Tooltip>
                }



                {
                    this.isPreviewEnabled() && (fileUpload.toType === 'video' || fileUpload.toType === 'av' || fileUpload.toType === 'audio') && <Tooltip text="Play Media">
                        <button
                            className={`play-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline x-btn-svg`}
                            disabled={!this.isFileReady(fileUpload)}
                            onClick={() => this.playVideo(fileUpload)}>
                            <img src="/icons/svg/play.svg" />
                        </button>
                    </Tooltip>
                }

                {
                    this.isPreviewEnabled() && (fileUpload.toType === 'pdf' || fileUpload.toType === 'jpg' || fileUpload.toType === 'png' || fileUpload.toType === 'jpeg' || fileUpload.toType === 'webp')
                    && <Tooltip text="View Media"><button
                        className={`view-btn x-btn x-btn-sm x-btn-gray x-btn-gray-outline x-btn-svg`}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.imagePreview(fileUpload)} >

                        <img src="img/material-icon/remove_red_eye-24px.svg" />
                    </button>
                    </Tooltip>

                }
            </div>
        </div>
    }

    render() {
        const shouldShowProgressbar = this.props.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED && this.props.fileUpload.ws
            && ((this.props.fileUpload.ws.processStage === 'processing' && this.props.fileUpload.ws.dataType === 'percentage')
                || (this.props.fileUpload.ws.processStage === 'upload' && this.props.fileUpload.ws.dataType === 'notification'));
        return <div className={`id${this.props.fileUpload.id} ${styles['card']}`}>
            <div className={styles['card-body']}>

                <div className={`${styles['card-title']}`}>
                    {this.getOutputFileNameIfAvailable(this.props.fileUpload)}
                </div>
                <div className={styles['card-content']}>
                    {
                        !this.props.fileUpload.readableMessage && !this.props.fileUpload.result && (!this.props.fileUpload.ws || !this.props.fileUpload.ws.processStage)
                        && <div className={styles['server-reply']}>
                            <div className={styles['loader']}>
                                <RippleLoaderComponent size={13}></RippleLoaderComponent>
                            </div> Process starting...
                        </div>
                    }
                    {
                        this.fileUpload.readableMessage
                        && (!this.fileUpload.ws || (this.fileUpload.ws && this.fileUpload.ws.processStage !== 'processing' && this.fileUpload.ws.dataType !== 'percentage'))
                        && <div className={styles['server-reply']}>
                            {
                                this.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED
                                && (!this.fileUpload.result || (this.fileUpload.result && !this.fileUpload.result.output))
                                && <div className={styles['loader']}>
                                    <RippleLoaderComponent size={13}></RippleLoaderComponent>
                                </div>
                            }
                            {this.props.fileUpload.readableMessage}
                        </div>
                    }

                    {
                        this.props.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED && !this.props.fileUpload.readableMessage && this.props.fileUpload.ws
                        && <div className={`${styles['completed']} ${styles['flashit']}`}>
                            {
                                !this.props.fileUpload.result || (this.props.fileUpload.result && !this.props.fileUpload.result.output) && <div className="loader">
                                    <RippleLoaderComponent size={13}></RippleLoaderComponent>
                                </div>
                            }
                            {this.decodeDataType(this.props.fileUpload, this.props.fileUpload.ws.dataType, this.props.fileUpload.ws.data)}
                        </div>
                    }

                    {
                        this.props.fileUpload.status === this.FILEUPLOAD_STATE_FAILED
                        && <div>
                            Upload failed...
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && <div>
                            {this.getProcessInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && this.isSingleFile(this.props.fileUpload.result.sizes.outputs)
                        && <div>
                            {this.getOutputInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.hasZipFileInfo
                        && <div>
                            {this.hasZipFileData}
                        </div>
                    }

                    {

                        shouldShowProgressbar && <ProgressComponent
                            value={Number(this.decodeData(this.props.fileUpload))}
                            postFix='%'
                        >
                        </ProgressComponent>
                    }
                </div>
                {
                    !this.props.hideFooterButtons && this.getFooterButtons(this.props.fileUpload)
                }
                
            </div>
            <div className={styles['social-share-promote']}>
                    Like this free service? Share and help us grow!

                    <div className={styles['social-share']}>
                        {
                            <div className={`fb-share-button ${styles['fb-share-button']}`}
                                data-href={`https://www.xconvert.com/${this.props.fileUpload.uniqueProcessType}`}
                                data-layout="button_count">
                            </div>
                        }
                        {
                            <div
                                className={styles["twitter-btn"]}
                                onClick={e => this.openTwitter()} >
                                <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.738 2h1.994L9.377 7.083 14.5 14h-4.011L7.346 9.805 3.751 14H1.757l4.658-5.437L1.5 2h4.113l2.84 3.834L11.738 2Zm-.7 10.781h1.105l-7.13-9.627H3.828l7.21 9.627Z" fill="#161616"></path></svg>
                                Twitter
                            </div>
                        }
                        {
                            <div
                                className={`${styles['linkedin-btn']}`}
                                onClick={e => this.shareOnLinkedIn()} >
                                <svg height="72" viewBox="0 0 72 72" width="72" xmlns="http://www.w3.org/2000/svg"><g fill="none" fillRule="evenodd"><path d="M8,72 L64,72 C68.418278,72 72,68.418278 72,64 L72,8 C72,3.581722 68.418278,-8.11624501e-16 64,0 L8,0 C3.581722,8.11624501e-16 -5.41083001e-16,3.581722 0,8 L0,64 C5.41083001e-16,68.418278 3.581722,72 8,72 Z" fill="#007EBB" /><path d="M62,62 L51.315625,62 L51.315625,43.8021149 C51.315625,38.8127542 49.4197917,36.0245323 45.4707031,36.0245323 C41.1746094,36.0245323 38.9300781,38.9261103 38.9300781,43.8021149 L38.9300781,62 L28.6333333,62 L28.6333333,27.3333333 L38.9300781,27.3333333 L38.9300781,32.0029283 C38.9300781,32.0029283 42.0260417,26.2742151 49.3825521,26.2742151 C56.7356771,26.2742151 62,30.7644705 62,40.051212 L62,62 Z M16.349349,22.7940133 C12.8420573,22.7940133 10,19.9296567 10,16.3970067 C10,12.8643566 12.8420573,10 16.349349,10 C19.8566406,10 22.6970052,12.8643566 22.6970052,16.3970067 C22.6970052,19.9296567 19.8566406,22.7940133 16.349349,22.7940133 Z M11.0325521,62 L21.769401,62 L21.769401,27.3333333 L11.0325521,27.3333333 L11.0325521,62 Z"
                                    fill="#FFF" /></g></svg>
                            </div>
                        }

                    </div>
                </div>
        </div>
    }

    openTwitter() {
        var popUp = window.open(`http://twitter.com/intent/tweet?url=https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}&text=${encodeURIComponent(this.props.fileUpload.pageData.title)}&via=xconvert_com&hashtags=xconvert`,
            'popupwindow',
            'scrollbars=yes,width=700,height=400'
        );
        popUp.focus();
        return false
    }

    shareOnLinkedIn() {
        var popUp = window.open(
            `http://www.linkedin.com/shareArticle?url=https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`,
            'popupwindow',
            'scrollbars=yes,width=700,height=400'
        );
        popUp.focus();
        return false
    }
}
